"use client"

import { X, Download, FileSpreadsheet } from "lucide-react"
import { Button } from "@/components/ui/button"

interface DownloadFormatModalProps {
  isOpen: boolean
  onClose: () => void
}

export function DownloadFormatModal({ isOpen, onClose }: DownloadFormatModalProps) {
  const handleDownload = (formatType: "school" | "class") => {
    // Create sample CSV data based on format type
    const headers =
      "Student Name,Class,Date of Birth,Gender,Guardian Name,Guardian Email,Guardian Phone,Address,Career Path,GPA"
    const sampleData =
      formatType === "school"
        ? [
            "Adebayo Oluwaseun,JSS1,2010-05-15,Male,Mrs. Adebayo,adebayo.guardian@email.com,+234-801-234-5678,12 Ikorodu Road Lagos,STEM,3.8",
            "Chioma Nwankwo,JSS2,2009-08-22,Female,Mr. Nwankwo,nwankwo.guardian@email.com,+234-802-345-6789,45 Enugu Street Abuja,Health Sciences,4.0",
            "Ibrahim Mohammed,JSS3,2008-12-10,Male,Malam Ibrahim,ibrahim.guardian@email.com,+234-803-456-7890,78 Kaduna Avenue Kano,Engineering,3.5",
          ]
        : [
            "Adebayo Oluwaseun,SS2,2007-05-15,Male,Mrs. Adebayo,adebayo.guardian@email.com,+234-801-234-5678,12 Ikorodu Road Lagos,STEM,3.8",
            "Chioma Nwankwo,SS2,2007-08-22,Female,Mr. Nwankwo,nwankwo.guardian@email.com,+234-802-345-6789,45 Enugu Street Abuja,Health Sciences,4.0",
          ]

    const csvContent = [headers, ...sampleData].join("\n")
    const blob = new Blob([csvContent], { type: "text/csv" })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `student_data_format_${formatType}_${new Date().getTime()}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)

    alert(`${formatType === "school" ? "Whole School" : "By Class"} format downloaded successfully!`)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50">
      {/* Blurry Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Modal Container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden animate-in zoom-in-95 duration-200">
          {/* Modal Header */}
          <div className="bg-indigo-600 px-6 py-4 flex justify-between items-center">
            <h3 className="text-lg leading-6 font-bold text-white">Download Data Format</h3>
            <button 
              onClick={onClose} 
              className="text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-full p-1 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="px-6 py-6">
            <p className="text-sm text-gray-600 mb-6">
              Download a sample Excel/CSV format to help you structure your student data correctly before uploading.
            </p>

            <div className="space-y-4">
              {/* Whole School Format */}
              <div className="border border-gray-300 rounded-lg p-4 hover:border-indigo-400 transition-colors hover:bg-indigo-50/30">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <FileSpreadsheet className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">Whole School Format</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Download format for uploading students from all classes at once
                      </p>
                      <Button
                        onClick={() => handleDownload("school")}
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Format
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              {/* By Class Format */}
              <div className="border border-gray-300 rounded-lg p-4 hover:border-teal-400 transition-colors hover:bg-teal-50/30">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <FileSpreadsheet className="w-5 h-5 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-1">By Class Format</h4>
                      <p className="text-xs text-gray-500 mb-3">
                        Download format for uploading students from a specific class
                      </p>
                      <Button
                        onClick={() => handleDownload("class")}
                        size="sm"
                        variant="outline"
                        className="border-teal-600 text-teal-600 hover:bg-teal-50 hover:border-teal-700 focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
                      >
                        <Download className="w-4 h-4 mr-2" />
                        Download Format
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Required Fields:</h4>
              <ul className="text-xs text-gray-600 space-y-1">
                <li>• Student Name - Full name of the student</li>
                <li>• Class - JSS1, JSS2, JSS3, SS1, SS2, SS3</li>
                {/* <li>• Date of Birth - Format: YYYY-MM-DD</li> */}
                <li>• Gender - Male or Female</li>
                <li>• Guardian Name - Full name of parent/guardian</li>
                <li>• Guardian Email - Valid email address</li>
                <li>• Guardian Phone - Nigerian phone format</li>
                <li>• Address - Residential address</li>
                <li>• Career Path - STEM, Arts, Business, Health Sciences, etc.</li>
                <li>• GPA - Grade Point Average (0.0 - 4.0)</li>
              </ul>
            </div>
          </div>
          
          {/* Modal Footer */}
          <div className="bg-gray-50 px-6 py-4 flex justify-end border-t border-gray-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              className="border-gray-300 hover:bg-gray-100 text-gray-700 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}